import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Bot, Wifi, WifiOff } from 'lucide-react';
import { List, Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { AgentStatus, AgentType } from './types.js';

const { Text } = Typography;

export interface WorkforceAgent {
  id?: string;
  name: string;
  status: AgentStatus;
  type?: AgentType;
  currentProject?: string;
}

export interface AgentStatusWidgetProps {
  agents?: readonly WorkforceAgent[];
  className?: string;
  testId?: string;
}

export function AgentStatusWidget({ agents = [], className, testId }: AgentStatusWidgetProps): ReactElement {
  const { t } = useTranslation();
  const runningCount = agents.filter((agent) => agent.status === 'running' || agent.status === 'active').length;
  
  const widgetContainerProps = {
    title: t('crm.dashboard.workforce.title'),
    icon: Bot,
    minHeight: 260,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: runningCount > 0 ? '#16a34a' : '#a8a29e' }}>
        {t('crm.dashboard.workforce.active_count', { count: runningCount })}
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {agents.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 opacity-50 h-[220px]">
          <Bot size={28} className="mb-2 text-stone-400" />
          <Text type="secondary" style={{ fontSize: 11 }}>{t('crm.dashboard.workforce.empty')}</Text>
        </div>
      ) : (
        <List
          size="small"
          dataSource={agents as WorkforceAgent[]}
          renderItem={(agent) => {
            const isActive = agent.status === 'active' || agent.status === 'running';

            return (
              <List.Item
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid #f5f5f4'
                }}
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="rounded-lg flex items-center justify-center"
                      style={{
                        width: 32,
                        height: 32,
                        background: isActive ? '#f0fdf4' : '#f5f5f4',
                        border: `1px solid ${isActive ? '#bbf7d0' : '#e7e5e4'}`,
                      }}
                    >
                      <Bot size={14} style={{ color: isActive ? '#16a34a' : '#a8a29e' }} />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: 13, color: '#44403c', display: 'block', lineHeight: 1.2 }}>
                        {agent.name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 2 }}>
                        {(agent.type ? t(`crm.dashboard.workforce.type.${agent.type}`) : t('crm.dashboard.workforce.type.agent'))}
                        {agent.currentProject ? ` · ${agent.currentProject}` : ''}
                      </Text>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isActive ? (
                      <Wifi size={12} style={{ color: '#16a34a' }} />
                    ) : (
                      <WifiOff size={12} style={{ color: '#a8a29e', opacity: 0.4 }} />
                    )}
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: isActive ? '#16a34a' : '#e7e5e4',
                        animation: isActive ? 'pulse 2s infinite' : 'none',
                      }}
                    />
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </WidgetContainer>
  );
}
